import React, { useState, useMemo } from 'react';
import {
  Film,
  ShoppingBag,
  Plane,
  Stethoscope,
  HeartHandshake,
  Home,
  Briefcase,
  Sun,
  UtensilsCrossed,
  Trophy,
  Activity,
  Laugh,
  Wine,
  Coffee,
  BookOpen,
  Sparkles,
  Utensils,
  Music,
  Landmark,
  PartyPopper,
  Check,
  Search,
  ShieldCheck,
  Star,
  MapPin,
  Heart,
  X,
  IndianRupee,
  CheckCircle2,
  ChevronRight,
  ArrowUpRight,
  Compass,
  Car
} from 'lucide-react';
import { Companion, SearchFilterState } from '../types';
import {
  isCompanionAvailableForService,
  getCompanionRateForService,
  getServicesWithCompanionCounts,
  ServiceItemWithCount
} from '../utils/companionServiceMatcher';
import { ALL_COMPANION_SERVICES } from '../data/servicesData';
import { isDemoCompanionProfile } from '../data/companionsData';

interface ChooseServiceSectionProps {
  companions: Companion[];
  selectedService: string | null;
  onSelectService: (serviceName: string | null) => void;
  wishlist: string[];
  onToggleWishlist: (id: string, e: React.MouseEvent) => void;
  onSelectCompanion: (companion: Companion, serviceName?: string) => void;
  onBookNow: (companion: Companion, e: React.MouseEvent, serviceName?: string) => void;
}

// Icon mapper for the 22 services
const getServiceIcon = (iconName: string, className: string = 'w-5 h-5') => {
  switch (iconName) {
    case 'Film':
      return <Film className={className} />;
    case 'ShoppingBag':
      return <ShoppingBag className={className} />;
    case 'Plane':
      return <Plane className={className} />;
    case 'Stethoscope':
      return <Stethoscope className={className} />;
    case 'HeartHandshake':
      return <HeartHandshake className={className} />;
    case 'Home':
      return <Home className={className} />;
    case 'Briefcase':
      return <Briefcase className={className} />;
    case 'Sun':
      return <Sun className={className} />;
    case 'UtensilsCrossed':
      return <UtensilsCrossed className={className} />;
    case 'Trophy':
      return <Trophy className={className} />;
    case 'Activity':
      return <Activity className={className} />;
    case 'Laugh':
      return <Laugh className={className} />;
    case 'Wine':
      return <Wine className={className} />;
    case 'Coffee':
      return <Coffee className={className} />;
    case 'BookOpen':
      return <BookOpen className={className} />;
    case 'Sparkles':
      return <Sparkles className={className} />;
    case 'Utensils':
      return <Utensils className={className} />;
    case 'Music':
      return <Music className={className} />;
    case 'Landmark':
      return <Landmark className={className} />;
    case 'PartyPopper':
      return <PartyPopper className={className} />;
    case 'Compass':
      return <Compass className={className} />;
    case 'Car':
      return <Car className={className} />;
    default:
      return <Sparkles className={className} />;
  }
};

const CATEGORIES = [
  'All (22)',
  'Care & Assistance',
  'Entertainment & Nightlife',
  'Dining & Social',
  'Sports & Active',
  'Lifestyle & Travel',
  'Learning & Business'
];

export const ChooseServiceSection: React.FC<ChooseServiceSectionProps> = ({
  companions,
  selectedService,
  onSelectService,
  wishlist,
  onToggleWishlist,
  onSelectCompanion,
  onBookNow
}) => {
  const [activeCategory, setActiveCategory] = useState<string>('All (22)');
  const [searchFilter, setSearchFilter] = useState<string>('');
  const [genderFilter, setGenderFilter] = useState<'all' | 'female' | 'male'>('all');
  const [sortFilter, setSortFilter] = useState<'rating' | 'price-low' | 'price-high'>('rating');

  // Compute live services with available counts
  const servicesWithCounts: ServiceItemWithCount[] = useMemo(() => {
    return getServicesWithCompanionCounts(companions);
  }, [companions]);

  // Filter services by category and search text
  const filteredServices = useMemo(() => {
    return servicesWithCounts.filter((srv) => {
      // Category filter
      if (activeCategory === 'Care & Assistance') {
        if (!['medical-help', 'elder-care', 'domestic-help'].includes(srv.id)) return false;
      } else if (activeCategory === 'Entertainment & Nightlife') {
        if (!['movies', 'comedy-club', 'going-to-bar', 'clubbing', 'music', 'parties'].includes(srv.id)) return false;
      } else if (activeCategory === 'Dining & Social') {
        if (!['dinner', 'hanging-out', 'baking-cooking', 'shopping'].includes(srv.id)) return false;
      } else if (activeCategory === 'Sports & Active') {
        if (!['sporting-events', 'playing-sports', 'outdoor-events'].includes(srv.id)) return false;
      } else if (activeCategory === 'Lifestyle & Travel') {
        if (!['travel', 'tourist-guide', 'driver', 'culture', 'outdoor-events', 'shopping'].includes(srv.id)) return false;
      } else if (activeCategory === 'Learning & Business') {
        if (!['business-events', 'tutoring'].includes(srv.id)) return false;
      }

      // Search query
      if (searchFilter.trim()) {
        const q = searchFilter.toLowerCase().trim();
        const matchesName = srv.name.toLowerCase().includes(q);
        const matchesDesc = srv.description.toLowerCase().includes(q);
        const matchesCat = srv.category.toLowerCase().includes(q);
        if (!matchesName && !matchesDesc && !matchesCat) return false;
      }

      return true;
    });
  }, [servicesWithCounts, activeCategory, searchFilter]);

  // Filter companions available for the selected service
  const availableCompanions = useMemo(() => {
    if (!selectedService) return [];

    return companions
      .filter((comp) => {
        // Must offer the service
        const isAvailable = isCompanionAvailableForService(comp, selectedService);
        if (!isAvailable) return false;

        // Gender filter
        if (genderFilter !== 'all' && comp.gender !== genderFilter) return false;

        return true;
      })
      .sort((a, b) => {
        const rateA = getCompanionRateForService(a, selectedService);
        const rateB = getCompanionRateForService(b, selectedService);

        if (sortFilter === 'price-low') {
          return rateA - rateB;
        } else if (sortFilter === 'price-high') {
          return rateB - rateA;
        }
        // default rating
        return b.rating - a.rating;
      });
  }, [companions, selectedService, genderFilter, sortFilter]);

  const activeServiceObj = useMemo(() => {
    if (!selectedService) return null;
    return servicesWithCounts.find(
      (s) => s.name.toLowerCase() === selectedService.toLowerCase() || s.id === selectedService.toLowerCase()
    );
  }, [selectedService, servicesWithCounts]);

  const handleCardClick = (serviceName: string) => {
    if (selectedService === serviceName) {
      // Toggle off
      onSelectService(null);
    } else {
      onSelectService(serviceName);
      // Smooth scroll to the results area
      setTimeout(() => {
        const resultsEl = document.getElementById('available-companions-results');
        if (resultsEl) {
          resultsEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  };

  return (
    <section id="choose-a-service-section" className="py-16 bg-white border-b border-slate-100 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-pink-50 border border-pink-100 text-pink-700 text-xs font-bold mb-3 shadow-xs">
            <Sparkles className="w-3.5 h-3.5 text-pink-600" />
            <span>22 Verified Companion Services Available</span>
          </div>

          <h2 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">
            Choose a Service
          </h2>

          <p className="text-base text-slate-600 mt-2">
            Select the type of service you need to view verified companions available for your occasion
          </p>

          {/* Quick Search & Clear */}
          <div className="mt-6 max-w-md mx-auto relative">
            <input
              type="text"
              placeholder="Search by service (e.g. Movies, Travel, Medical Help)..."
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              className="w-full pl-10 pr-10 py-2.5 bg-slate-50 border border-slate-200 rounded-2xl text-xs sm:text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:bg-white transition-all shadow-xs"
            />
            <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-3" />
            {searchFilter && (
              <button
                type="button"
                onClick={() => setSearchFilter('')}
                className="absolute right-3 top-2.5 p-1 text-slate-400 hover:text-slate-600 rounded-full cursor-pointer"
                title="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Category Filters Pills */}
        <div className="flex items-center gap-2 overflow-x-auto pb-3 mb-8 scrollbar-none justify-start sm:justify-center">
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              type="button"
              onClick={() => setActiveCategory(cat)}
              className={`px-3.5 py-1.5 rounded-full text-xs font-bold shrink-0 transition-all cursor-pointer ${
                activeCategory === cat
                  ? 'bg-pink-600 text-white shadow-md shadow-pink-600/20'
                  : 'bg-slate-100 hover:bg-slate-200 text-slate-600 border border-slate-200/60'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* 22 Services Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3.5 sm:gap-4 mb-12">
          {filteredServices.map((srv) => {
            const isSelected = selectedService?.toLowerCase() === srv.name.toLowerCase();

            return (
              <div
                key={srv.id}
                id={`service-card-${srv.id}`}
                onClick={() => handleCardClick(srv.name)}
                className={`group relative p-4 rounded-2xl border transition-all duration-300 cursor-pointer flex flex-col justify-between select-none ${
                  isSelected
                    ? 'bg-gradient-to-br from-pink-50 via-rose-50/50 to-white border-pink-500 shadow-lg shadow-pink-500/15 ring-2 ring-pink-500'
                    : 'bg-white hover:bg-slate-50/80 border-slate-200 hover:border-pink-300 shadow-xs hover:shadow-md'
                }`}
              >
                <div>
                  {/* Top Bar: Icon & Available count badge */}
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <div
                      className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors ${
                        isSelected
                          ? 'bg-pink-600 text-white shadow-sm'
                          : 'bg-pink-50 group-hover:bg-pink-100 text-pink-600'
                      }`}
                    >
                      {getServiceIcon(srv.iconName, 'w-5 h-5')}
                    </div>

                    <span
                      className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                        isSelected
                          ? 'bg-pink-600 text-white'
                          : 'bg-slate-100 group-hover:bg-pink-50 text-slate-600 group-hover:text-pink-700'
                      }`}
                    >
                      {srv.availableCompanionsCount} Available
                    </span>
                  </div>

                  {/* Service Title */}
                  <h3
                    className={`font-bold text-sm mb-1 line-clamp-1 transition-colors ${
                      isSelected ? 'text-pink-700' : 'text-slate-900 group-hover:text-pink-600'
                    }`}
                  >
                    {srv.name}
                  </h3>

                  {/* Service Description */}
                  <p className="text-[11px] text-slate-500 line-clamp-2 leading-relaxed mb-3">
                    {srv.description}
                  </p>
                </div>

                {/* Bottom Row: Starting Rate & Active checkmark */}
                <div className="pt-2 border-t border-slate-100 flex items-center justify-between text-xs">
                  <span className="font-semibold text-slate-700 flex items-center gap-0.5">
                    <span className="text-[10px] text-slate-400 font-normal">From</span>
                    <span className="font-bold text-slate-900">₹{srv.startingPrice}</span>
                    <span className="text-[10px] text-slate-400">/hr</span>
                  </span>

                  <span
                    className={`text-[11px] font-bold flex items-center gap-0.5 transition-colors ${
                      isSelected ? 'text-pink-600' : 'text-slate-400 group-hover:text-pink-500'
                    }`}
                  >
                    {isSelected ? (
                      <span className="flex items-center gap-1 text-pink-600 bg-pink-100 px-1.5 py-0.5 rounded-md text-[10px]">
                        <Check className="w-3 h-3 stroke-[3]" /> Selected
                      </span>
                    ) : (
                      <span className="group-hover:translate-x-0.5 transition-transform flex items-center">
                        Select <ChevronRight className="w-3 h-3" />
                      </span>
                    )}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Selected Service Available Companions Display */}
        <div id="available-companions-results" className="scroll-mt-24">
          {selectedService ? (
            <div className="bg-slate-50/80 border border-pink-100 rounded-3xl p-5 sm:p-7 shadow-xs">
              
              {/* Header for Results */}
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-5 border-b border-slate-200">
                <div className="space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="px-2.5 py-0.5 bg-pink-100 text-pink-700 text-xs font-extrabold rounded-full uppercase tracking-wider">
                      Live Matching
                    </span>
                    <h3 className="text-xl sm:text-2xl font-black text-slate-900">
                      Available Companions for {activeServiceObj?.name || selectedService}
                    </h3>
                  </div>

                  <p className="text-xs sm:text-sm text-slate-600">
                    {activeServiceObj?.description} • {availableCompanions.length} verified companions available now
                  </p>
                </div>

                {/* Controls: Gender Filter, Sort, & Clear */}
                <div className="flex flex-wrap items-center gap-2.5">
                  {/* Gender Selector */}
                  <div className="inline-flex p-1 bg-white rounded-xl border border-slate-200 text-xs font-semibold">
                    <button
                      type="button"
                      onClick={() => setGenderFilter('all')}
                      className={`px-3 py-1 rounded-lg transition-colors cursor-pointer ${
                        genderFilter === 'all' ? 'bg-pink-600 text-white font-bold' : 'text-slate-600 hover:text-slate-900'
                      }`}
                    >
                      All ({availableCompanions.length})
                    </button>
                    <button
                      type="button"
                      onClick={() => setGenderFilter('female')}
                      className={`px-3 py-1 rounded-lg transition-colors cursor-pointer ${
                        genderFilter === 'female' ? 'bg-pink-600 text-white font-bold' : 'text-slate-600 hover:text-slate-900'
                      }`}
                    >
                      Girlfriends
                    </button>
                    <button
                      type="button"
                      onClick={() => setGenderFilter('male')}
                      className={`px-3 py-1 rounded-lg transition-colors cursor-pointer ${
                        genderFilter === 'male' ? 'bg-pink-600 text-white font-bold' : 'text-slate-600 hover:text-slate-900'
                      }`}
                    >
                      Boyfriends
                    </button>
                  </div>

                  {/* Sort Selector */}
                  <select
                    value={sortFilter}
                    onChange={(e) => setSortFilter(e.target.value as any)}
                    className="px-3 py-1.5 bg-white border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-pink-500"
                  >
                    <option value="rating">Top Rated ⭐</option>
                    <option value="price-low">Price: Low to High</option>
                    <option value="price-high">Price: High to Low</option>
                  </select>

                  {/* Clear Filter Button */}
                  <button
                    type="button"
                    onClick={() => onSelectService(null)}
                    className="px-3 py-1.5 bg-white hover:bg-slate-100 border border-slate-200 text-slate-600 hover:text-red-600 text-xs font-bold rounded-xl flex items-center gap-1 transition-colors cursor-pointer"
                    title="Clear selected service"
                  >
                    <X className="w-3.5 h-3.5" />
                    <span>Clear Service</span>
                  </button>
                </div>
              </div>

              {/* Companions Grid */}
              {availableCompanions.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5 mt-6">
                  {availableCompanions.map((companion) => {
                    const isWishlisted = wishlist.includes(companion.id);
                    const serviceRate = getCompanionRateForService(companion, selectedService);

                    return (
                      <div
                        key={companion.id}
                        id={`available-companion-${companion.id}`}
                        onClick={() => onSelectCompanion(companion, selectedService)}
                        className="group bg-white rounded-2xl overflow-hidden border border-slate-200 hover:border-pink-300 shadow-xs hover:shadow-xl transition-all duration-300 flex flex-col cursor-pointer"
                      >
                        {/* Image Container */}
                        <div className="relative aspect-[4/4.2] w-full overflow-hidden bg-slate-100">
                          <img
                            src={companion.image}
                            alt={companion.name}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                            loading="lazy"
                            referrerPolicy="no-referrer"
                          />

                          {/* Top Badges */}
                          <div className="absolute top-2.5 left-2.5 flex flex-col gap-1">
                            {isDemoCompanionProfile(companion) ? (
                              <span className="bg-amber-950/80 backdrop-blur-md text-amber-300 border border-amber-500/40 text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1 shadow-sm">
                                <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                                Showcase Only
                              </span>
                            ) : (
                              <span className="bg-black/60 backdrop-blur-md text-emerald-300 text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1 shadow-sm">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                                Available
                              </span>
                            )}
                            <span className="bg-pink-600/90 backdrop-blur-md text-white text-[10px] font-extrabold px-2 py-0.5 rounded-full shadow-sm">
                              {selectedService}
                            </span>
                          </div>

                          {/* Wishlist Heart */}
                          <button
                            type="button"
                            onClick={(e) => onToggleWishlist(companion.id, e)}
                            className={`absolute top-2.5 right-2.5 w-8 h-8 rounded-full flex items-center justify-center transition-all cursor-pointer ${
                              isWishlisted
                                ? 'bg-pink-500 text-white shadow-md'
                                : 'bg-white/80 hover:bg-white text-slate-600 hover:text-pink-600 backdrop-blur-xs'
                            }`}
                            title={isWishlisted ? 'Remove from favorites' : 'Save to favorites'}
                          >
                            <Heart className={`w-4 h-4 ${isWishlisted ? 'fill-white' : ''}`} />
                          </button>

                          {/* Quick Book on Hover */}
                          {!isDemoCompanionProfile(companion) ? (
                            <div className="absolute inset-x-3 bottom-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                              <button
                                type="button"
                                onClick={(e) => onBookNow(companion, e, selectedService)}
                                className="w-full py-2 bg-gradient-to-r from-pink-600 to-rose-600 hover:from-pink-700 hover:to-rose-700 text-white text-xs font-bold rounded-xl shadow-lg shadow-pink-600/40 transition-transform active:scale-95 cursor-pointer"
                              >
                                Book for {selectedService} ✨
                              </button>
                            </div>
                          ) : (
                            <div className="absolute inset-x-3 bottom-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                              <div className="w-full py-1.5 bg-slate-900/80 backdrop-blur-xs text-amber-300 text-[10px] font-bold rounded-xl text-center border border-amber-500/30">
                                Showcase Profile • Not Bookable
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Card Info */}
                        <div className="p-4 flex flex-col flex-1 justify-between">
                          <div>
                            {/* Name & Verification */}
                            <div className="flex items-center gap-1.5 mb-1">
                              <h4 className="font-bold text-slate-900 text-sm sm:text-base group-hover:text-pink-600 transition-colors line-clamp-1">
                                {companion.name}, {companion.age}
                              </h4>
                              {companion.isVerified && (
                                <span title="Verified Profile">
                                  <CheckCircle2 className="w-4 h-4 text-sky-500 shrink-0" />
                                </span>
                              )}
                            </div>

                            {/* Location & Rating */}
                            <div className="flex items-center gap-3 text-xs text-slate-500 mb-2">
                              <div className="flex items-center gap-1">
                                <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                                <span className="font-bold text-slate-800">{companion.rating}</span>
                                <span className="text-slate-400">({companion.reviewsCount})</span>
                              </div>

                              <div className="flex items-center gap-1 truncate">
                                <MapPin className="w-3.5 h-3.5 text-pink-500 shrink-0" />
                                <span className="truncate">{companion.city}</span>
                              </div>
                            </div>

                            {/* Bio Snippet */}
                            <p className="text-xs text-slate-600 line-clamp-2 leading-relaxed mb-3">
                              {companion.bio}
                            </p>
                          </div>

                          {/* Service Hourly Charge */}
                          <div className="pt-2.5 border-t border-slate-100 flex items-center justify-between">
                            <div>
                              <span className="text-[10px] text-slate-400 block">Rate for {selectedService}:</span>
                              <div className="flex items-baseline gap-1">
                                <span className="text-base font-extrabold text-slate-900">
                                  ₹{serviceRate.toLocaleString('en-IN')}
                                </span>
                                <span className="text-[11px] text-slate-500">/hr</span>
                              </div>
                            </div>

                            {isDemoCompanionProfile(companion) ? (
                              <span className="px-2.5 py-1 bg-slate-100 text-slate-500 text-[10px] font-bold rounded-lg cursor-not-allowed">
                                Showcase
                              </span>
                            ) : (
                              <button
                                type="button"
                                onClick={(e) => onBookNow(companion, e, selectedService)}
                                className="px-3 py-1.5 bg-pink-50 hover:bg-pink-600 text-pink-600 hover:text-white text-xs font-bold rounded-xl transition-colors cursor-pointer flex items-center gap-1"
                              >
                                <span>Book</span>
                                <ArrowUpRight className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12 bg-white rounded-2xl border border-slate-200 mt-6">
                  <p className="text-sm font-bold text-slate-700">
                    No companions found matching the "{genderFilter !== 'all' ? genderFilter : ''}" filter for {selectedService}.
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    Try switching gender filter to "All" or browse other available services above.
                  </p>
                  <button
                    type="button"
                    onClick={() => setGenderFilter('all')}
                    className="mt-3 px-4 py-2 bg-pink-600 text-white text-xs font-bold rounded-xl hover:bg-pink-700 transition-colors"
                  >
                    Reset Filter
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="p-6 rounded-2xl bg-slate-50 border border-slate-200/80 text-center">
              <p className="text-xs sm:text-sm text-slate-600">
                👉 Click on any of the <span className="font-bold text-slate-900">22 services above</span> (such as Movies, Shopping, Travel, Medical Help, Tutoring) to instantly view verified available companions for that activity.
              </p>
            </div>
          )}
        </div>

      </div>
    </section>
  );
};
